import { canvasSink } from '../h264/h264_decoder.ts';
import type { AccessUnit } from '../h264/h264_depacketizer.ts';
import { H264Pipeline, type PipelineStats, type PipelineTrack } from '../h264/h264_pipeline.ts';
import type { FromPipelineWorker, ToPipelineWorker } from './pipeline_protocol.ts';

/**
 * Where a stream is depacketised, decoded and painted:
 * - `worker`: all three in the stream's Worker, painting to an `OffscreenCanvas` (the design).
 * - `worker-main-paint`: decoded in the Worker, pictures sent to the page and painted there
 *   (for an engine without a 2D `OffscreenCanvas` in Workers).
 * - `main`: everything on the main thread (for an engine without `VideoDecoder` in Workers).
 */
export type PipelineMode = 'worker' | 'worker-main-paint' | 'main';

/** The page's handle on the pipeline of one stream, wherever it runs. */
export interface VideoPipeline {
    readonly mode: PipelineMode;
    /** Why a less capable mode than the requested one is in use; null when it is the requested one. */
    readonly fallbackReason: string | null;
    start(track: PipelineTrack) : void;
    /** RTP packets and their arrival time (`wallNow()`). Takes over the packets' buffers. */
    push(packets: Uint8Array[], receivedAt: number) : void;
    /** Whole access units, for a stream that gives them so (HLS). */
    pushUnits(units: AccessUnit[]) : void;
    end() : void;
    /** The latest numbers; from a Worker they are up to `STATS_INTERVAL_MS` old. */
    stats() : PipelineStats | null;
    /** Ends everything; the canvas keeps its last picture. */
    close() : void;
}

export interface VideoPipelineOptions {
    /** `auto` (default) takes the most capable mode the engine offers. */
    mode?: PipelineMode | 'auto';
    log?: (message: string) => void;
    /** How long the Worker may take to start before the main thread is used instead. Default 5 s. */
    workerTimeoutMs?: number;
}

function mainPipeline(canvas: HTMLCanvasElement, log: (message: string) => void, fallbackReason: string | null) : VideoPipeline {
    const pipeline = new H264Pipeline(canvasSink(canvas), log);

    return {
        mode: 'main',
        fallbackReason,
        start: (track) => pipeline.start(track),
        push: (packets, receivedAt) => pipeline.push(packets, receivedAt),
        pushUnits: (units) => pipeline.pushUnits(units),
        end: () => pipeline.end(),
        stats: () => pipeline.stats(),
        close: () => pipeline.end(),
    };
}

/**
 * Creates the pipeline for a canvas. With a Worker mode the canvas is handed over to the Worker
 * for good (`transferControlToOffscreen`), so use a canvas that nothing else draws on.
 */
export async function createVideoPipeline(
    canvas: HTMLCanvasElement,
    options: VideoPipelineOptions = {},
) : Promise<VideoPipeline> {
    const requested = options.mode ?? 'auto';
    const log = options.log ?? (() => {});
    if (requested === 'main') {
        return mainPipeline(canvas, log, null);
    }
    if (typeof Worker !== 'function') {
        return mainPipeline(canvas, log, 'no Worker in this engine');
    }

    let starting: Worker | undefined;
    let ready: Extract<FromPipelineWorker, { type: 'ready' }>;
    try {
        const created = new Worker(new URL('./pipeline_worker.ts', import.meta.url), { type: 'module' });
        starting = created;
        ready = await new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('the Worker did not start')), options.workerTimeoutMs ?? 5000);
            created.onmessage = (event: MessageEvent<FromPipelineWorker>) => {
                if (event.data.type !== 'ready') {
                    return;
                }

                clearTimeout(timer);
                resolve(event.data);
            };
            created.onerror = (event) => {
                clearTimeout(timer);
                reject(new Error(event.message || 'the Worker failed to load'));
            };
        });
    } catch (error) {
        starting?.terminate();

        return mainPipeline(canvas, log, error instanceof Error ? error.message : String(error));
    }
    const worker = starting;
    if (!ready.videoDecoder) {
        worker.terminate();

        return mainPipeline(canvas, log, 'no VideoDecoder in a Worker');
    }

    let mode: PipelineMode = 'worker';
    let fallbackReason: string | null = null;
    if (requested === 'worker-main-paint') {
        mode = 'worker-main-paint';
    } else if (!ready.offscreenCanvas2d) {
        fallbackReason = 'no 2D OffscreenCanvas in a Worker';
    } else if (typeof canvas.transferControlToOffscreen !== 'function') {
        fallbackReason = 'the canvas cannot be transferred';
    }
    if (fallbackReason !== null) {
        mode = 'worker-main-paint';
    }

    const post = (message: ToPipelineWorker, transfer: Transferable[] = []) : void => worker.postMessage(message, transfer);
    let latest: PipelineStats | null = null;
    let session = 0;
    let paint: ((frame: VideoFrame) => void) | null = null;
    if (mode === 'worker') {
        const offscreen = canvas.transferControlToOffscreen();
        post({ type: 'output', canvas: offscreen }, [offscreen]);
    } else {
        paint = canvasSink(canvas);
        post({ type: 'output', canvas: null });
    }

    worker.onerror = (event) => log(`video: pipeline worker error (${event.message})`);
    worker.onmessage = (event: MessageEvent<FromPipelineWorker>) => {
        const message = event.data;
        if (message.type === 'stats') {
            if (message.session === session) {
                latest = message.stats;
            }
        } else if (message.type === 'frame') {
            paint?.(message.frame);
        } else if (message.type === 'log') {
            log(message.message);
        }
    };

    return {
        mode,
        fallbackReason,
        start(track) {
            latest = null;
            post({ type: 'start', session: ++session, track });
        },
        push(packets, receivedAt) {
            // Several packets are usually views on one transport chunk; each buffer is listed once.
            post({ type: 'rtp', packets, receivedAt }, [...new Set(packets.map((packet) => packet.buffer as ArrayBuffer))]);
        },
        pushUnits: (units) => post({ type: 'units', units }),
        end: () => post({ type: 'end' }),
        stats: () => latest,
        close: () => worker.terminate(),
    };
}
